import { formatDate } from '@angular/common';
import { EventEmitter, inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { Logger } from '@df/core/service/logger';
import { TimeoutService } from '@df/ng/timeout.service';
import { clearInterval, setInterval } from 'worker-timers';
import { IdleExpiry } from './core/expiry/expiry';
import { IdleExpiryLocalStorageService } from './core/expiry/expiry-local-storage.service';
import { IdleInterrupt } from './core/interrupt/interrupt';
import { IdleInterruptArgs } from './core/interrupt/interrupt-args';
import { IdleInterruptSource } from './core/interrupt/interrupt-source';
import { IdlePingService } from './core/ping/ping.service';
import { IDLE_OPTIONS, NIdle } from './idle.namespace';

/**
 * A service for detecting and responding to user idleness.
 */
@Injectable({
    providedIn: 'root'
})
export class IdleService implements OnDestroy {
    onIdleStart$ = new EventEmitter<boolean>();
    onTimeoutWarning$ = new EventEmitter<number>();
    onTimeout$ = new EventEmitter<number>();
    onInterrupt$ = new EventEmitter();

    protected logger = inject(Logger);
    protected timeoutService = inject(TimeoutService);
    protected request = inject(RequestService);
    protected app = inject(App);
    protected expiry = inject(IdleExpiry);
    protected ping = inject(IdlePingService);
    protected readonly options = inject(IDLE_OPTIONS);
    protected platformId = inject(PLATFORM_ID, { optional: true });

    private idle = 0;
    private timeoutVal = 0;
    private autoResume?: NIdle.EAutoResume;
    private interrupts: IdleInterrupt[] = [];
    private running = false;
    private idling = false;
    private idleHandle?: number;
    private timeoutHandle?: number;
    private countdown = 0;
    private pingEnabled = false;
    private idlePingEnabled = false;

    get canRun(): boolean {
        return this.app.isPlatformBrowser;
    }

    /**
     * Called by Angular when destroying the instance.
     */
    ngOnDestroy(): void {
        this.stop();
        this.clearInterrupts();
    }

    appInitialize(): void {
        if (this.options.enabled && this.app.isPlatformBrowser) {
            this.setTimeout(this.options.timeout);
            this.setIdle(this.options.idle);
            this.setAutoResume(this.options.autoResume);
            this.setInterrupts(this.options.interrupts);

            if (this.ping) {
                if (this.options.pingInterval) {
                    this.setPingEnabled(true);

                    this.ping.interval(this.options.pingInterval);
                }

                if (this.options.idlePingInterval) {
                    this.setIdlePingEnabled(true);

                    this.ping.idleInterval(this.options.idlePingInterval);
                }
            }

            this.watch();
        }

        this.setIdling(false);
    }

    onIdleStart(): void {
        this.onIdleStart$.emit(true);

        this.logger.warn(`Idle started at ${new Date()}.`);
    }

    onIdleEnd(): void {
        this.onIdleStart$.emit(false);

        this.logger.log('Idle ended.');

        this.ping?.onIdlePing();
    }

    onTimeoutWarning(value: number): void {
        this.onTimeoutWarning$.emit(value);

        this.logger.log(`Idle timeout warning: ${value} seconds remaining. ${formatDate(new Date(), 'dd/MM/yyyy HH:mm:ss', 'en')}`);
    }

    onTimeout(): void {
        this.onTimeout$.emit();

        this.request.send(undefined, () => this.request.nexus.customer.logout({}));

        this.logger.log('Idle timeout.');
    }

    onInterrupt(eventArgs?: any): void {
        this.onInterrupt$.emit(eventArgs);

        // this.logger.log(`Interrupt: ${eventArgs?.type}. ${formatDate(new Date(), 'dd/MM/yyyy HH:mm:ss', 'en')}`);

        if (eventArgs?.type === 'visibilitychange') {
            this.ping?.onIdlePing();
        }
    }

    /**
     * Sets the idle name for localStorage.
     * Important to set if multiple instances of Idle with LocalStorageExpiry
     * @param key The name of the idle.
     */
    setIdleName(key: string): void {
        if (this.expiry instanceof IdleExpiryLocalStorageService) {
            this.expiry.setIdleName(key);
        } else {
            throw new Error('Cannot set expiry key name because no LocalStorageExpiry has been provided.');
        }
    }

    /**
     * Returns whether or not ping integration is enabled.
     * @return True if integration is enabled; otherwise, false.
     */
    getPingEnabled(): boolean {
        return this.pingEnabled;
    }

    /**
     * Sets and returns whether or not ping integration is enabled.
     * @param value True if the integration is enabled; otherwise, false.
     * @return The current value.
     */
    setPingEnabled(value: boolean): boolean {
        if (!this.ping) {
            throw new Error('Cannot enable ping integration because no Ping has been provided.');
        }

        return (this.pingEnabled = value);
    }

    /**
     * Returns whether or not ping integration is enabled.
     * @return True if integration is enabled; otherwise, false.
     */
    getIdlePingEnabled(): boolean {
        return this.idlePingEnabled;
    }

    /**
     * Sets and returns whether or not ping integration is enabled.
     * @param value True if the integration is enabled; otherwise, false.
     * @return The current value.
     */
    setIdlePingEnabled(value: boolean): boolean {
        if (!this.ping) {
            throw new Error('Cannot enable idle ping integration because no Ping has been provided.');
        }

        return (this.idlePingEnabled = value);
    }

    /**
     * Returns the current timeout value.
     * @return The timeout value in seconds.
     */
    getTimeout(): number | undefined {
        return this.timeoutVal;
    }

    /**
     * Sets the timeout value.
     * @param seconds - The timeout value in seconds. 0 or false to disable timeout feature.
     * @return The current value. If disabled, the value will be 0.
     */
    setTimeout(seconds: number | boolean): number {
        if (seconds === false) {
            this.timeoutVal = 0;
        } else if (typeof seconds === 'number' && seconds >= 0) {
            this.timeoutVal = seconds;
        } else {
            throw new Error('"seconds" can only be "false" or a positive number.');
        }

        this.logger.log(`Timeout value is ${this.timeoutVal} seconds.`);

        return this.timeoutVal;
    }

    /**
     * Returns the current idle value.
     * @return The idle value in seconds.
     */
    getIdling(): number | undefined {
        return this.idle;
    }

    /**
     * Sets the idle value.
     * @param seconds - The idle value in seconds.
     * @return The idle value in seconds.
     */
    setIdle(seconds: number): number {
        if (seconds <= 0) {
            throw new Error('"seconds" must be greater zero');
        }

        this.logger.log(`Idle value is ${seconds} seconds.`);

        return (this.idle = seconds);
    }

    /**
     * Returns the current auto resume value.
     * @return The current value.
     */
    getAutoResume(): NIdle.EAutoResume | undefined {
        return this.autoResume;
    }

    setAutoResume(value: NIdle.EAutoResume): NIdle.EAutoResume {
        return (this.autoResume = value);
    }

    /**
     * Sets interrupts from the specified sources.
     * @param sources - Interrupt sources.
     * @return The resulting interrupts.
     */
    setInterrupts(sources: IdleInterruptSource[]): IdleInterrupt[] | undefined {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        this.clearInterrupts();

        const self = this;

        for (const source of sources) {
            const options = { platformId: this.platformId };
            const sub = new IdleInterrupt(source, options);

            sub.subscribe((args: IdleInterruptArgs) => {
                self.interrupt(args.force, args.innerArgs);
            });

            this.interrupts.push(sub);
        }

        return this.interrupts;
    }

    /**
     * Returns the current interrupts.
     * @return The current interrupts.
     */
    getInterrupts(): Array<IdleInterrupt> {
        return this.interrupts;
    }

    /**
     * Pauses, unsubscribes, and clears the current interrupt subscriptions.
     */
    clearInterrupts(): void {
        for (const sub of this.interrupts) {
            sub.pause();
            sub.unsubscribe();
        }

        this.interrupts.length = 0;
    }

    /**
     * Returns whether or not the service is running i.e. watching for idleness.
     * @return True if service is watching; otherwise, false.
     */
    isRunning(): boolean {
        return this.running;
    }

    /**
     * Returns whether or not the user is considered idle.
     * @return True if the user is in the idle state; otherwise, false.
     */
    isIdling(): boolean {
        return this.idling;
    }

    /**
     * Starts watching for inactivity.
     */
    watch(skipExpiry?: boolean): void {
        if (!this.canRun) {
            return;
        }

        this.safeClearInterval('idleHandle');
        this.safeClearInterval('timeoutHandle');

        const timeout = !this.timeoutVal ? 0 : this.timeoutVal;

        if (!skipExpiry) {
            const value = new Date(this.expiry.now().getTime() + (this.idle + timeout) * 1000);

            this.expiry.last(value);
        }

        if (this.idling) {
            this.toggleState();
        }

        if (!this.running) {
            this.startPing();
            this.toggleInterrupts(true);
        }

        this.running = true;

        // this.logger.log(`Watching for inactivity. ${formatDate(new Date(), 'dd/MM/yyyy HH:mm:ss', 'en')}`);

        const watchFn = () => {
            const diff = this.getExpiryDiff(timeout);

            if (diff > 0) {
                this.safeClearInterval('idleHandle');
                this.setIdleIntervalOutsideOfZone(watchFn, 1000);
            } else {
                this.toggleState();
            }
        };

        this.setIdleIntervalOutsideOfZone(watchFn, 1000);
    }

    /**
     * Allows protractor tests to call waitForAngular without hanging
     */
    setIdleIntervalOutsideOfZone(watchFn: () => void, frequency: number): void {
        this.idleHandle = setInterval(watchFn, frequency);
    }

    /**
     * Stops watching for inactivity.
     */
    stop(): void {
        this.stopPing();
        this.stopIdlePing();

        this.toggleInterrupts(false);

        this.safeClearInterval('idleHandle');
        this.safeClearInterval('timeoutHandle');

        this.setIdling(false);
        this.running = false;

        this.expiry.last(null);
    }

    /**
     * Forces a timeout event and state.
     */
    timeout(): void {
        this.stopPing();

        this.toggleInterrupts(false);

        this.safeClearInterval('idleHandle');
        this.safeClearInterval('timeoutHandle');

        this.setIdling(true);
        this.running = false;
        this.countdown = 0;

        this.onTimeout();
    }

    /**
     * Signals that user activity has occurred.
     * @param force - Forces watch to be called, unless they are timed out.
     * @param eventArgs - Optional source event arguments.
     */
    interrupt(force?: boolean, eventArgs?: any): void {
        if (!this.running) {
            return;
        }

        if (this.timeoutVal && this.expiry.isExpired()) {
            this.timeout();
            return;
        }

        this.onInterrupt(eventArgs);

        if (
            force === true ||
            this.autoResume === NIdle.EAutoResume.idle ||
            (this.autoResume === NIdle.EAutoResume.notIdle && !this.expiry.idling())
        ) {
            this.watch(force);
        }
    }

    private setIdling(value: boolean): void {
        this.idling = value;
        this.expiry.idling(value);
    }

    private toggleState(): void {
        if (this.canRun) {
            this.setIdling(!this.idling);
        }

        if (this.idling) {
            this.onIdleStart();
            this.stopPing();
            this.startIdlePing();

            if (this.timeoutVal > 0) {
                this.countdown = this.timeoutVal;
                this.doCountdown();
                this.setTimeoutIntervalOutsideZone(() => {
                    this.doCountdownInZone();
                }, 1000);
            }
        } else {
            this.toggleInterrupts(true);
            this.onIdleEnd();
            this.stopIdlePing();
            this.startPing();
        }

        this.safeClearInterval('idleHandle');
    }

    private setTimeoutIntervalOutsideZone(intervalFn: () => void, frequency: number) {
        this.timeoutHandle = setInterval(() => {
            intervalFn();
        }, frequency);
    }

    private toggleInterrupts(resume: boolean): void {
        if (this.app.isPlatformBrowser) {
            for (const interrupt of this.interrupts) {
                if (resume) {
                    interrupt.resume();
                } else {
                    interrupt.pause();
                }
            }
        }
    }

    private getExpiryDiff(timeout: number): number {
        const now: Date = this.expiry.now();
        const last: Date = this.expiry.last() || now;

        return last.getTime() - now.getTime() - timeout * 1000;
    }

    private doCountdownInZone(): void {
        this.timeoutService.platformSafe(() => {
            this.doCountdown();
        });
    }

    private doCountdown(): void {
        const diff = this.getExpiryDiff(this.timeoutVal);

        if (diff > 0) {
            this.safeClearInterval('timeoutHandle');
            this.interrupt(true);
            return;
        }

        if (!this.idling) {
            return;
        }

        if (this.countdown <= 0) {
            this.timeout();
            return;
        }

        this.onTimeoutWarning(this.countdown);
        this.countdown--;
    }

    private safeClearInterval(handleName: string): void {
        const handle = this[handleName];

        if (handle !== null && typeof handle !== 'undefined') {
            clearInterval(this[handleName]);
            this[handleName] = null;
        }
    }

    private startPing(): void {
        if (!this.ping || !this.pingEnabled) {
            return;
        }

        if (this.running) {
            this.ping.ping();
        }

        this.ping.start();
    }

    private startIdlePing(): void {
        if (!this.ping || !this.idlePingEnabled) {
            return;
        }

        if (!this.running) {
            this.ping.idlePing();
        }

        this.ping.idleStart();
    }

    private stopPing(): void {
        if (!this.ping || !this.pingEnabled) {
            return;
        }

        this.ping.stop();
    }

    private stopIdlePing(): void {
        if (!this.ping || !this.idlePingEnabled) {
            return;
        }

        this.ping.idleStop();
    }
}
